import { RightOutlineIcon } from '@carboncopyco/icons';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useMemo } from 'react';
import { ColorHex } from '../../constants';
import { TextButton } from '../Typography';

export interface PanelProps {
  isActive?: boolean;
  header?: string | React.ReactNode;
  disabled?: boolean;
  children: React.ReactNode;
  collapsible?: boolean;
  showIcon?: boolean;
  panelKey?: string;
  panelIndex?: string;
  borderd?: boolean;
  onItemClick?: (
    key: string,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => void;
  noPadding?: boolean;
}

export const Panel = ({
  header,
  children,
  showIcon = true,
  borderd = true,
  isActive,
  onItemClick,
  panelKey,
  panelIndex,
  noPadding,
}: PanelProps) => {
  const panelHeader = useMemo(() => {
    if (typeof header === 'string') {
      return (
        <TextButton
          label={header}
          color="text-ds-natural-600"
        />
      );
    }
    return <div className="text-ds-natural-600">{header}</div>;
  }, [header]);

  return (
    <>
      <div
        className={classNames([
          'p-4 flex justify-between items-center w-full bg-ds-accent3-100 hover:bg-ds-natural-50 hover:text-ds-natural-500 transition duration-300 ease-in-out cursor-pointer',
          borderd && panelIndex !== '0' && 'border-t border-ds-natural-150',
          'w-auto',
        ])}
        onClick={(e) => {
          if (panelKey && typeof onItemClick === 'function') {
            onItemClick(panelKey, e);
          }
        }}
      >
        {panelHeader}
        {showIcon && (
          <motion.div
            className="text-ds-natural-600"
            key="icon"
            animate={{
              rotate: isActive ? 90 : 0,
            }}
          >
            <RightOutlineIcon
              size={16}
              color={ColorHex.natural600}
            />
          </motion.div>
        )}
      </div>
      <AnimatePresence initial={false}>
        {isActive && (
          <motion.div
            className="overflow-hidden"
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { height: 'auto' },
              collapsed: { height: 0 },
            }}
            transition={{
              duration: 0.5,
              ease: [0.04, 0.62, 0.23, 0.98],
            }}
          >
            <div
              className={classNames(['overflow-hidden', noPadding && 'p-4'])}
            >
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
